import { z } from "zod";
// Mirrors @ferrivia/db's enums — api-contract has no dependency on
// @ferrivia/db (it's shared with browser code), so they're duplicated here
// rather than imported, the same way patients.ts duplicates authMethodSchema.
export const sexSchema = z.enum(["female", "male"]);
export const lifeStageSchema = z.enum([
    "menstruating",
    "planning_pregnancy",
    "trying_to_conceive",
    "fertility_treatment",
    "pregnant",
    "postpartum",
    "perimenopause",
    "menopause",
    "postmenopause",
]);
export const consentKindSchema = z.enum([
    "terms_and_privacy",
    "health_data_processing",
    "lab_partner_result_upload",
    "health_questionnaire_collection",
]);
// One per question the flow asks, in the order it asks them. Also the unit
// `missing` reports in, so the client can resume at the first unanswered one.
export const onboardingStepSchema = z.enum([
    "name",
    "sex",
    "age",
    "life_stage",
    "location",
    "email",
    "consents",
]);
export const onboardingAnswersSchema = z.object({
    email: z.string().nullable(),
    // Stored as a year, asked as an age — see 008_onboarding.sql. The client
    // renders `currentYear - birthYear` back into the age field.
    birthYear: z.number().int().nullable(),
    sex: sexSchema.nullable(),
    lifeStages: z.array(lifeStageSchema),
    // Distinct from `lifeStages.length > 0`: an empty selection is a valid
    // answer, and this is what says it was given.
    lifeStagesAnswered: z.boolean(),
    country: z.string().nullable(),
    city: z.string().nullable(),
    // Collected in the US only; Finland never sets it.
    region: z.string().nullable().optional(),
});
export const consentStateSchema = z.record(consentKindSchema, z.boolean());
// What the client shows behind each consent checkbox, and what the server
// stamps onto the consent row so the record says which text was agreed to.
export const consentDocumentSchema = z.object({
    url: z.string(),
    version: z.string(),
});
export const onboardingStatusSchema = z.object({
    status: z.enum(["incomplete", "complete"]),
    // Server-authoritative, derived from the API's own ENVIRONMENT. The client
    // must not decide this from its build-time VITE_APP_ENV: staging and PR
    // previews both talk to the development API, so a build-time flag and a
    // runtime one would be two truths free to drift.
    answersRequired: z.boolean(),
    // Read-only, from the eID identity. Onboarding confirms the name rather
    // than asking for it.
    identity: z.object({
        givenName: z.string().nullable(),
        familyName: z.string().nullable(),
    }),
    answers: onboardingAnswersSchema,
    consents: consentStateSchema,
    documents: z.record(consentKindSchema, consentDocumentSchema),
    // Empty when nothing is outstanding. Always computed against what prod
    // would require, so a dev client still shows an honest progress state.
    missing: z.array(onboardingStepSchema),
});
// Every field optional: the client PATCHes one step at a time. `null` is not
// accepted for a field — clearing an answer is not something the flow does,
// and allowing it would make a partial update indistinguishable from an
// erasure.
export const onboardingUpdateSchema = z
    .object({
    email: z.string().email(),
    // The age as typed. The server turns it into a birth year, so the two
    // never disagree about which year "34" meant.
    age: z.number().int().min(18).max(120),
    sex: sexSchema,
    lifeStages: z.array(lifeStageSchema),
    country: z.string().regex(/^[A-Z]{2}$/),
    city: z.string().min(1).max(100),
    region: z.string().regex(/^[A-Z]{2}-[A-Z0-9]{1,3}$/),
    givenName: z.string().trim().min(1).max(100),
    familyName: z.string().trim().min(1).max(100),
    consents: z.array(z.object({
        kind: consentKindSchema,
        granted: z.boolean(),
    })),
})
    .partial();
